import { Button, Col, Label, Row } from 'reactstrap';
import { H3, H6 } from '../../../../AbstractElements';
import { useLocation } from 'react-router-dom';
import { RootState } from '../../../../ReduxToolkit/Store';
import { useSelector } from 'react-redux';
import { incomeExpenses } from '../FormData/FormData';
import { useCallback, useEffect, useState } from 'react';
import TextField from '../../../common/TextField';
import DollarInput from '../../../common/DollarInput';
import { useGetRequestQuery } from '../../../../Hooks/useGetRequestQuery';
import { getClientMMPIncomeExpensesByClientId, getDashboardService, insertClientMMPIncomeExpenses } from '../../../../Services/MMPClientServices/IncomeAndExpenses';
import { useMutationRequestQuery } from '../../../../Hooks/useMutationRequestQuery';
import { toast } from 'react-toastify';
import IncomeExpensesForm from './IncomeExpensesForm';
import DecimalInput from '../../../common/DecimalInput';

const formatWithCommas = (number: number) => number.toLocaleString();

const calculateIncomeExpenses = (formData: any) => {
    const parseNumber = (value: string | null) => {
        if (value == null || value.trim() === '') return 0;
        return parseFloat(value.replace(/,/g, '') || '0');
    };

    const totalIncomeAfterTax = parseNumber(formData.investmentNetAfterTax) + parseNumber(formData.otherIncomeAfterTax) + parseNumber(formData.clientName1Income) + parseNumber(formData.clientName2Income);

    const totalExpenses = parseNumber(formData.expensesLiving) + parseNumber(formData.loanRepayments) + parseNumber(formData.otherLiabilitiesloanRepayments) + parseNumber(formData.motorVehiclesloanRepayments);

    const savingsForInvestment = totalIncomeAfterTax - totalExpenses;


    const actualSavingsRate = totalIncomeAfterTax !== 0 ? ((totalIncomeAfterTax - totalExpenses) / totalIncomeAfterTax) * 100 : 0;

    return {
        totalIncomeAfterTax: formatWithCommas(totalIncomeAfterTax),
        totalExpenses: formatWithCommas(totalExpenses),
        savingsForInvestment: formatWithCommas(savingsForInvestment),
        actualSavingsRate: actualSavingsRate.toFixed(0),
    };
};

export default function IncomeExpenses({ clientFactsFindData }: any) {
    const location = useLocation();
    const userData = useSelector((state: RootState) => state.auth);
    const profileData = useSelector((state: RootState) => state.profile);
    const [formData, setFormData] = useState<any>({ ...incomeExpenses, targetSavingsRate: '20' });

    const isMMPAssetsLiabilitiesRoute = location.pathname === '/MMPAssetsLiabilities';

    const parseNumber = (value: string | null) => {
        if (value == null || value.trim() === "") return 0;
        return parseFloat(value.replace(/,/g, "") || "0");
    };

    const handleValueChange = (name: string, value: string) => {
        setFormData((prevData: any) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleMonthlyTotalChange = (totalMonthlySum: number) => {
        const formattedValue = totalMonthlySum.toLocaleString();
        setFormData((prevData: any) => ({
            ...prevData,
            expensesLiving: formattedValue,
        }));
    };

    const {
        data: clientMMPIncomeExpenseDetailsResponse
    } = useGetRequestQuery<any>(
        ['getDashboardService', userData?.nameidentifier],
        () => getDashboardService(userData?.nameidentifier as string),
        {
            enabled: !!userData?.nameidentifier,
        }
    );

    const {
        data: clientMMPIncomeExpensesResponse,
        isLoading: isClientMMPIncomeExpensesLoading,
        isError: isClientMMPIncomeExpensesError,
    } = useGetRequestQuery<any>(
        ['getClientMMPIncomeExpensesByClientId', userData?.nameidentifier],
        () => getClientMMPIncomeExpensesByClientId(userData?.nameidentifier as string),
        {
            enabled: !!userData?.nameidentifier,
        }
    );

    useEffect(() => {
        if (clientMMPIncomeExpensesResponse?.data &&
            !isClientMMPIncomeExpensesError &&
            !isClientMMPIncomeExpensesLoading &&
            typeof clientMMPIncomeExpensesResponse?.data === "object") {

            setFormData((prevData: any) => ({
                ...prevData,
                ...clientMMPIncomeExpensesResponse?.data
            }));
        }
    }, [clientMMPIncomeExpensesResponse, isClientMMPIncomeExpensesError, isClientMMPIncomeExpensesLoading]);


    useEffect(() => {
        if (clientMMPIncomeExpenseDetailsResponse?.data) {
            setFormData((prevFormData: any) => ({
                ...prevFormData,
                loanRepayments: formatWithCommas(clientMMPIncomeExpenseDetailsResponse?.data[12]?.amount)?.toString() || "0",
                motorVehiclesloanRepayments: formatWithCommas(clientMMPIncomeExpenseDetailsResponse?.data[13]?.amount)?.toString() || "0",
                otherLiabilitiesloanRepayments: formatWithCommas(clientMMPIncomeExpenseDetailsResponse?.data[14]?.amount)?.toString() || "0"
            }));
        }
    }, [clientMMPIncomeExpenseDetailsResponse]);


    const { mutate: insertClientIncomeExpenses, isPending: isInsertClientIncomeExpensesPending } = useMutationRequestQuery(
        'insertClientIncomeExpenses',
        insertClientMMPIncomeExpenses,
        {
            onError: (error: any) => {
                toast.error('Failed to submit Income and Expenses information', {
                    autoClose: 2000,
                });
            },
        }
    );
    const handleSave = () => {
        insertClientIncomeExpenses({ ...formData, clientId: parseInt(userData?.nameidentifier as any) });
    };

    const memoizedCalculateIncomeExpenses = useCallback(() => calculateIncomeExpenses(formData), [formData]);

    // ... existing code ...

    useEffect(() => {
        const totals = memoizedCalculateIncomeExpenses();
        const totalExpenses = parseNumber(formData.totalExpenses);
        setFormData((prevData: any) => ({
            ...prevData,
            ...totals,
            expenseLivingPercent: totalExpenses !== 0 ? ((parseNumber(formData.expensesLiving) / totalExpenses) * 100).toFixed(0) : '0',
            loanRepaymentHomePercent: totalExpenses !== 0 ? ((parseNumber(formData.loanRepayments) / totalExpenses) * 100).toFixed(0) : '0',
            loanRepaymentMotorVehicalPercent: totalExpenses !== 0 ? ((parseNumber(formData.motorVehiclesloanRepayments) / totalExpenses) * 100).toFixed(0) : '0',
            loanRepaymentsOtherLiabilitiesPercent: totalExpenses !== 0 ? ((parseNumber(formData.otherLiabilitiesloanRepayments) / totalExpenses) * 100).toFixed(0) : '0',
        }));
    }, [
        formData.investmentNetAfterTax,
        formData.otherIncomeAfterTax,
        formData.expensesLiving,
        formData.loanRepayments,
        formData.totalExpenses,
        formData.clientName1Income,
        formData.clientName2Income,
        formData.otherLiabilitiesloanRepayments,
        formData.motorVehiclesloanRepayments,
    ]);

    return (
        <>
            <div className={isMMPAssetsLiabilitiesRoute ? 'mmp-assets-liabilities container-fluid' : 'container-fluid'} onBlur={handleSave}>
                <div className="table-responsive h-100 py-2">
                    <section className='bg-white'>
                        <div className="scrollable-table-container">
                            <table className="scrollable-table" style={{ marginLeft: '1%', marginTop: '3%' }}>
                                <div className="d-flex align-items-center">
                                    <Col xs={12} className='d-flex'>
                                        <H3 className="heading-color f-w-900 text-left">Income & Expenses - Monthly</H3>
                                    </Col>
                                </div>
                                <Row className='mt-4'>
                                    <div className='d-flex'>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '15%', paddingInline: '2px' }}>
                                            <H6 className="heading-color f-w-900 text-left">Net Income After Tax Paid</H6>
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '15%', paddingInline: '2px' }}>
                                            <H6 className="heading-color f-w-900 text-left"></H6>
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '20%', paddingInline: '2px' }}>
                                            <H6 className="heading-color f-w-900 text-left">Expenses - Monthly</H6>
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '13%', paddingInline: '2px' }}>
                                            <H6 className="heading-color f-w-900 text-left"></H6>
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '10%', paddingInline: '2px' }}>
                                            <H6 className="heading-color f-w-900 text-left">%</H6>
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '13%', paddingInline: '2px' }}>
                                            <H6 className="heading-color f-w-900 text-left">Savings for Investment</H6>
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '40%', paddingInline: '2px' }}>
                                            <H6 className="heading-color f-w-900 text-left"></H6>
                                        </Col>
                                    </div>
                                    <div className='d-flex item-center mt-4'>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '15%', paddingInline: '2px' }}>
                                            <Label className='mb-3'>Client 1 Net Income</Label>
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '15%', paddingInline: '2px' }}>
                                            <DollarInput
                                                label=""
                                                name="clientName1Income"
                                                value={formData.clientName1Income}
                                                setValue={handleValueChange}
                                                placeholder=""
                                                design="flex"
                                                limit={6}
                                                tabIndex={1}
                                                passCol='col-9'
                                            />
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '20%', paddingInline: '2px' }}>
                                            <Label className='mb-3'>Expenses - Living</Label>
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '13%', paddingInline: '2px' }}>
                                            <DollarInput
                                                label=""
                                                name="expensesLiving"
                                                value={formData.expensesLiving}
                                                setValue={handleValueChange}
                                                placeholder=""
                                                design="flex"
                                                limit={5}
                                                tabIndex={6}
                                                disabled
                                                passCol='col-10'
                                            />
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '10%', paddingInline: '2px' }}>
                                            <DecimalInput
                                                label=""
                                                name="expenseLivingPercent"
                                                value={formData.expenseLivingPercent}
                                                setValue={handleValueChange}
                                                placeholder=""
                                                design="flex"
                                                limit={5}
                                                tabIndex={6}
                                                disabled
                                                passCol='col-10'
                                            />
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '13%', paddingInline: '2px' }}>
                                            <Label className='mb-3'>Savings for Investment</Label>
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '13%', paddingInline: '2px' }}>
                                            <DollarInput
                                                label=""
                                                name="savingsForInvestment"
                                                value={formData.savingsForInvestment}
                                                setValue={handleValueChange}
                                                placeholder=""
                                                design="flex"
                                                limit={5}
                                                tabIndex={9}
                                                style={{ fontWeight: '800' }}
                                                disabled
                                                passCol='col-10'
                                            />
                                        </Col>
                                    </div>
                                    <div className='d-flex item-center'>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '15%', paddingInline: '2px' }}>
                                            <Label className='mb-3'>Client 2 Net Income</Label>
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '15%', paddingInline: '2px' }}>
                                            {/* <TextField
                                                label=""
                                                type="text"
                                                name="clientName2Income"
                                                value={formData.clientName2Income}
                                                setValue={handleValueChange}
                                                placeholder=""
                                                design="flex"
                                                maxChars={15}
                                                passCol='col-9'
                                                tabIndex={2}
                                            /> */}

                                            <DollarInput
                                                label=""
                                                name="clientName2Income"
                                                value={formData.clientName2Income}
                                                setValue={handleValueChange}
                                                placeholder=""
                                                design="flex"
                                                limit={6}
                                                tabIndex={3}
                                                passCol='col-9'
                                            />
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '20%', paddingInline: '2px' }}>
                                            <Label className='mb-3'>Loan  Repayment - Home
                                            </Label>
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '13%', paddingInline: '2px' }}>
                                            <DollarInput
                                                label=""
                                                name="loanRepayments"
                                                value={formData.loanRepayments}
                                                setValue={handleValueChange}
                                                placeholder=""
                                                design="flex"
                                                limit={5}
                                                tabIndex={7}
                                                disabled
                                                passCol='col-10'
                                            />
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '10%', paddingInline: '2px' }}>
                                            <DecimalInput
                                                label=""
                                                name="loanRepaymentHomePercent"
                                                value={formData.loanRepaymentHomePercent}
                                                setValue={handleValueChange}
                                                placeholder=""
                                                design="flex"
                                                limit={5}
                                                tabIndex={6}
                                                disabled
                                                passCol='col-10'
                                            />
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '13%', paddingInline: '2px' }}>
                                            <Label className='mb-3'>Target Savings Rate
                                            </Label>
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '13%', paddingInline: '2px' }}>
                                            <DecimalInput
                                                label=""
                                                name="targetSavingsRate"
                                                value={formData.targetSavingsRate || '20.00'}
                                                setValue={handleValueChange}
                                                placeholder=""
                                                design="flex"
                                                limit={4}
                                                tabIndex={10}
                                                style={{ fontWeight: '800' }}
                                                disabled
                                                passCol='col-10'
                                            />
                                        </Col>
                                    </div>
                                    <div className='d-flex item-center '>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '15%', paddingInline: '2px' }}>
                                            <Label className='mb-3'>Property Income Net after Tax</Label>
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '15%', paddingInline: '2px' }}>
                                            <DollarInput
                                                label=""
                                                name="investmentNetAfterTax"
                                                value={formData.investmentNetAfterTax}
                                                setValue={handleValueChange}
                                                placeholder=""
                                                design="flex"
                                                limit={6}
                                                tabIndex={3}
                                                passCol='col-9'
                                            />
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '20%', paddingInline: '2px' }}>
                                            <Label className='mb-3'>Loan  Repayment - Motor Vehicles
                                            </Label>
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '13%', paddingInline: '2px' }}>
                                            <DollarInput
                                                label=""
                                                name="motorVehiclesloanRepayments"
                                                value={formData.motorVehiclesloanRepayments}
                                                setValue={handleValueChange}
                                                placeholder=""
                                                design="flex"
                                                limit={5}
                                                tabIndex={7}
                                                disabled
                                                passCol='col-10'
                                            />
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '10%', paddingInline: '2px' }}>
                                            <DecimalInput
                                                label=""
                                                name="loanRepaymentMotorVehicalPercent"
                                                value={formData.loanRepaymentMotorVehicalPercent}
                                                setValue={handleValueChange}
                                                placeholder=""
                                                design="flex"
                                                limit={5}
                                                tabIndex={6}
                                                disabled
                                                passCol='col-10'
                                            />
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '13%', paddingInline: '2px' }}>
                                            <Label className='mb-3'>Actual Savings Rate

                                            </Label>
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '13%', paddingInline: '2px' }}>
                                            <DecimalInput
                                                label=""
                                                name="actualSavingsRate"
                                                value={formData.actualSavingsRate}
                                                setValue={handleValueChange}
                                                placeholder=""
                                                design="flex"
                                                limit={4}
                                                disabled
                                                tabIndex={11}
                                                style={{ fontWeight: '800' }}
                                                passCol='col-10'
                                            />
                                        </Col>
                                    </div>
                                    <div className='d-flex item-center'>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '15%', paddingInline: '2px' }}>
                                            <Label className='mb-3'>Other Income after Tax</Label>
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '15%', paddingInline: '2px' }}>
                                            <DollarInput
                                                label=""
                                                name="otherIncomeAfterTax"
                                                value={formData.otherIncomeAfterTax}
                                                setValue={handleValueChange}
                                                placeholder=""
                                                design="flex"
                                                limit={6}
                                                tabIndex={4}
                                                passCol='col-9'
                                            />
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '20%', paddingInline: '2px' }}>
                                            <Label className='mb-3'>Loan  Repayment - Other Liabilities</Label>
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '13%', paddingInline: '2px' }}>
                                            <DollarInput
                                                label=""
                                                name="otherLiabilitiesloanRepayments"
                                                value={formData.otherLiabilitiesloanRepayments}
                                                setValue={handleValueChange}
                                                placeholder=""
                                                design="flex"
                                                limit={5}
                                                tabIndex={7}
                                                disabled

                                                passCol='col-10'
                                            />
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '10%', paddingInline: '2px' }}>
                                            <DecimalInput
                                                label=""
                                                name="loanRepaymentsOtherLiabilitiesPercent"
                                                value={formData.loanRepaymentsOtherLiabilitiesPercent}
                                                setValue={handleValueChange}
                                                placeholder=""
                                                design="flex"
                                                limit={5}
                                                tabIndex={6}
                                                disabled
                                                passCol='col-10'
                                            />
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '13%', paddingInline: '2px' }}>
                                            <Label className='mb-3'>Result

                                            </Label>
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '13%', paddingInline: '2px' }}>
                                            <TextField
                                                label=""
                                                type='number'
                                                name="targetSavingsRate"
                                                value={formData.result}
                                                disabled
                                                setValue={handleValueChange}
                                                placeholder=""
                                                design="flex"
                                                tabIndex={1}
                                                passCol='col-5'
                                                style={{
                                                    backgroundColor:
                                                        (parseFloat(formData.targetSavingsRate) || 0) > (parseFloat(formData.actualSavingsRate) || 0)
                                                            ? 'red'
                                                            : 'green',
                                                }}
                                            />
                                        </Col>
                                    </div>
                                    <div className='d-flex item-center'>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '15%', paddingInline: '2px' }}>
                                            <Label className='f-w-900'>Total Income After Tax</Label>
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '15%', paddingInline: '2px' }}>
                                            <DollarInput
                                                label=""
                                                name="totalIncomeAfterTax"
                                                value={formData.totalIncomeAfterTax}
                                                setValue={handleValueChange}
                                                placeholder=""
                                                design="flex"
                                                limit={8}
                                                style={{ fontWeight: '800' }}
                                                disabled
                                                tabIndex={5}
                                                passCol='col-9'
                                            />
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '20%', paddingInline: '2px' }}>
                                            <Label className='f-w-900'>Total Expenses
                                            </Label>
                                        </Col>
                                        <Col xs={12} className='responsive-col-expense' style={{ width: '13%', paddingInline: '2px' }}>
                                            <DollarInput
                                                label=""
                                                name="totalExpenses"
                                                value={formData.totalExpenses}
                                                setValue={handleValueChange}
                                                placeholder=""
                                                design="flex"
                                                limit={8}
                                                tabIndex={1}
                                                style={{ fontWeight: '800' }}
                                                disabled
                                                passCol='col-10'
                                            />
                                        </Col>
                                    </div>
                                </Row>
                            </table>

                        </div>
                    </section>
                </div>
            </div>
            <IncomeExpensesForm
                expensesLiving={formData.expensesLiving}
                insertClientIncomeExpenses={insertClientIncomeExpenses}
                onMonthlyTotalChange={handleMonthlyTotalChange} // Pass callback
                incomeExpensesData={formData}
            />

        </>
    );
}

